import { ticketService } from '@services/ticket.service';
import voiceService from '@services/voice.service';
import type { WrapUp } from '@stores/voiceAgentStore';
import { callClock } from './callConsole';

export type WrapUpOutcome = 'resolved' | 'follow_up' | 'call_back';

/** Each outcome, as the agent picks it and as the ticket records it. */
export const WRAP_UP_OUTCOMES: ReadonlyArray<{ value: WrapUpOutcome; label: string; hint: string; status: string }> = [
  { value: 'resolved', label: 'Resolved', hint: 'Sorted on the call', status: 'resolved' },
  { value: 'follow_up', label: 'Needs follow-up', hint: 'Work continues on the ticket', status: 'in_progress' },
  { value: 'call_back', label: 'Call back later', hint: 'The caller is waiting to hear back', status: 'pending' },
];

/** The internal note the wrap-up leaves on the ticket. */
export function wrapUpNote(wrapUp: WrapUp, outcome: WrapUpOutcome, notes: string): string {
  const label = WRAP_UP_OUTCOMES.find((o) => o.value === outcome)!.label;
  const lines = [
    `Voice call wrap-up: ${label}`,
    `Caller ${wrapUp.customerPhone || 'unknown'} · ${callClock(wrapUp.durationSeconds)} with an agent`,
  ];
  if (notes.trim()) lines.push('', notes.trim());
  return lines.join('\n');
}

/**
 * Save the wrap-up on the call's ticket: an internal note with the outcome and
 * the agent's notes, then the ticket status the outcome implies. Returns the
 * ticket id. Throws when the call has no ticket to save to.
 */
export async function saveWrapUp(wrapUp: WrapUp, outcome: WrapUpOutcome, notes: string): Promise<string> {
  const { ticketId, conversationId } = await callConversation(wrapUp);
  await ticketService.sendMessage(ticketId, conversationId, {
    content: wrapUpNote(wrapUp, outcome, notes),
    isInternal: true,
  });
  await ticketService.updateStatus(ticketId, WRAP_UP_OUTCOMES.find((o) => o.value === outcome)!.status);
  return ticketId;
}

/**
 * The agent handed the call on (transferred it, or left a call a colleague
 * keeps): the call is not over, so there is no outcome and the ticket's status
 * is left alone. Only what they wrote during the call goes to the ticket, as
 * an internal note. Nothing is sent when they wrote nothing.
 */
export async function saveHandoverNote(call: WrapUp, notes: string, toName?: string): Promise<boolean> {
  const text = notes.trim();
  if (!text) return false;
  const { ticketId, conversationId } = await callConversation(call);
  const heading = toName ? `Notes before handing the call to ${toName}` : 'Notes before handing the call on';
  await ticketService.sendMessage(ticketId, conversationId, {
    content: `${heading} (caller ${call.customerPhone || 'unknown'}, ${callClock(call.durationSeconds)}):\n\n${text}`,
    isInternal: true,
  });
  return true;
}

/** The call's ticket, and the conversation on it a note about the call belongs in. */
async function callConversation(wrapUp: WrapUp): Promise<{ ticketId: string; conversationId: string }> {
  let ticketId = wrapUp.ticketId;
  if (!ticketId && wrapUp.sessionId) {
    try {
      ticketId = (await voiceService.getSession(wrapUp.sessionId)).ticketId;
    } catch {
      // Falls through to "no ticket".
    }
  }
  if (!ticketId) throw new Error('This call has no ticket to save to yet.');

  // Found the way the ticket page finds it: the ticket carries its
  // conversations (there is no single-conversation route). The call's own
  // phone conversation is preferred.
  const ticket: any = await ticketService.getTicket(ticketId);
  const conversations: Array<{ id: string; channel?: string }> = ticket?.conversations ?? [];
  const conversation =
    ticket?.conversation ||
    conversations.find((c) => c.channel?.toLowerCase() === 'phone') ||
    conversations[0];
  if (!conversation?.id) throw new Error('This ticket has no conversation to add the note to.');
  return { ticketId, conversationId: conversation.id };
}
